import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar, Footer } from "@/components/Layout";
import { ShareButtons } from "@/components/ShareButtons";
import { api } from "@/lib/api";
import { StarIcon, ArrowRightIcon, QuotesIcon } from "@phosphor-icons/react";

const Stars = ({ value, size = 18 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <StarIcon key={n} size={size} weight={n <= value ? "fill" : "regular"} className="text-gold-500" />
    ))}
  </div>
);

export default function Ratings() {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/reviews/public")
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-navy-900 text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-gold-500 mb-4">
              Work performed
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight max-w-3xl">
              Rated by the customers we've fixed things for.
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-6" data-testid="ratings-summary">
              <div className="flex items-center gap-3">
                <span className="font-heading text-5xl font-extrabold text-gold-500">
                  {data.average || "—"}
                </span>
                <div>
                  <Stars value={Math.round(data.average)} size={20} />
                  <p className="text-sm text-slate-400 mt-1">
                    {data.count} verified {data.count === 1 ? "review" : "reviews"}
                  </p>
                </div>
              </div>
              <Link
                to="/request"
                data-testid="ratings-cta"
                className="ml-auto rounded-sm bg-gold-600 hover:bg-gold-700 text-white text-sm font-semibold px-6 py-3 transition-colors active:scale-95 inline-flex items-center gap-2"
              >
                Get a Quote <ArrowRightIcon size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </section>

        {/* Reviews grid */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          {loading ? (
            <p className="text-slate-400 text-center py-16">Loading reviews…</p>
          ) : data.reviews.length === 0 ? (
            <div className="text-center py-20" data-testid="ratings-empty">
              <p className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Reviews coming soon
              </p>
              <p className="text-slate-600 max-w-md mx-auto">
                We're gathering feedback from recent jobs. Check back shortly to see what
                customers say about our work.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ratings-grid">
              {data.reviews.map((r, i) => (
                <motion.div
                  key={i}
                  data-testid={`review-card-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: (i % 3) * 0.06 }}
                  className="bg-white border border-slate-200 rounded-sm p-6 flex flex-col hover:-translate-y-1 hover:shadow-crisp-amber transition-transform"
                >
                  <QuotesIcon size={28} weight="fill" className="text-gold-500/40 mb-3" />
                  <Stars value={r.rating} />
                  {r.comment && (
                    <p className="mt-4 text-slate-700 leading-relaxed flex-1">"{r.comment}"</p>
                  )}
                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <p className="font-heading font-semibold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500 capitalize">
                      {[r.service, r.property_type].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {r.reply && (
                    <div className="mt-4 border-l-2 border-gold-500 pl-3" data-testid={`review-reply-${i}`}>
                      <p className="text-xs font-semibold text-gold-700">Response from MCC Service Corp.</p>
                      <p className="text-sm text-slate-600 mt-0.5">{r.reply}</p>
                    </div>
                  )}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wide font-bold text-slate-400">Share</span>
                    <ShareButtons
                      tone="light"
                      url={typeof window !== "undefined" ? `${window.location.origin}/ratings` : "/ratings"}
                      text={`${r.rating}★ review of MCC Service Corp.${r.comment ? ` — "${r.comment}"` : ""}`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
